import { defineStore } from "pinia"

export interface ConfirmOptions {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  /** When true, hides the cancel button — alert/notify style. */
  alertOnly?: boolean
}

interface ConfirmRequest extends Required<Omit<ConfirmOptions, "destructive" | "alertOnly">> {
  destructive: boolean
  alertOnly: boolean
  resolve: (ok: boolean) => void
}

interface State {
  active: ConfirmRequest | null
}

/**
 * Themed replacement for `window.confirm()`.
 *
 * Usage:
 *   if (!(await useConfirmStore().request("Delete this reminder?"))) return
 *   if (!(await useConfirmStore().request({
 *     title: "Delete account",
 *     message: "This is permanent.",
 *     destructive: true,
 *   }))) return
 *
 * The actual modal lives in <ConfirmDialog>, mounted globally inside App.vue.
 */
export const useConfirmStore = defineStore("confirm", {
  state: (): State => ({ active: null }),

  actions: {
    request(input: string | ConfirmOptions): Promise<boolean> {
      const opts: ConfirmOptions = typeof input === "string" ? { message: input } : input

      // If a dialog is already up, resolve it as cancelled before opening a new one.
      this.active?.resolve(false)

      return new Promise<boolean>((resolve) => {
        this.active = {
          title: opts.title ?? "Are you sure?",
          message: opts.message,
          confirmLabel: opts.confirmLabel ?? "Confirm",
          cancelLabel: opts.cancelLabel ?? "Cancel",
          destructive: opts.destructive ?? false,
          alertOnly: opts.alertOnly ?? false,
          resolve,
        }
      })
    },

    notify(input: string | Omit<ConfirmOptions, "alertOnly" | "destructive" | "cancelLabel">): Promise<void> {
      const opts = typeof input === "string" ? { message: input } : input
      return this.request({
        title: opts.title ?? "Heads up",
        message: opts.message,
        confirmLabel: opts.confirmLabel ?? "OK",
        alertOnly: true,
      }).then(() => undefined)
    },

    answer(ok: boolean) {
      const active = this.active
      if (!active) return
      this.active = null
      active.resolve(ok)
    },
  },
})
